import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import { FacetSection } from "@/features/search/components/facets/FacetSection";
import { useSearchContext } from "@/features/search/context/SearchContext";
import { useTaxonomyRows } from "@/features/search/hooks/useTaxonomyRows";
import { formatResultCount } from "@/features/search/utils/count-placeholder.utils";
import { isTermLocked } from "@/features/search/utils/facets.utils";
import {
  type TaxonomyRow,
  getCarriedQuery,
} from "@/features/search/utils/taxonomy.utils";
import { toHref } from "@/features/search/utils/url.utils";

/*
 * Indentation caps out: a deep tree should stay readable in a 16rem sidebar, and past three
 * steps the extra offset says less than the row order already does.
 */
const INDENT = ["pl-0", "pl-3", "pl-6", "pl-9"] as const;
const indentClass = (depth: number) =>
  INDENT[Math.min(depth, INDENT.length - 1)];

/**
 * How a row navigates.
 *
 * `landing-page` when the category belongs to the page itself — it can't be expressed as a
 * query param, so each row goes to the category's own landing page. `filter` otherwise
 * (/new, /sale, a query search): there the category is the user's, so it stays a param and
 * the page's own character — new, discounted, matching "chair" — survives the move.
 */
type Mode = "landing-page" | "filter";

const Row = ({ row, mode }: { row: TaxonomyRow; mode: Mode }) => {
  const { query, locale } = useRouter();
  const { navigation } = useSearchContext();

  /*
   * The underline belongs to the title, not the row: the count is not part of the link's
   * name, and underlining it reads as if the number were clickable too. Driven off the
   * anchor's `group` so hovering anywhere on the row still marks the title.
   */
  const label = (
    <>
      <span className="flex-1 truncate group-hover:underline">{row.title}</span>
      <span className="tabular-nums text-grey-400">
        {formatResultCount(row.count ?? 0, locale)}
      </span>
    </>
  );

  /* No dimming for empty categories: they aren't offered at all (see `getTaxonomyRows`). */
  const rowClass = cn(
    "flex items-center space-x-2 py-1 font-body text-sm",
    indentClass(row.depth),
    row.isCurrent ? "font-semibold text-black" : "text-grey-600"
  );

  const href =
    mode === "landing-page"
      ? row.destination && {
          pathname: toHref(row.destination.url),
          query: getCarriedQuery(query),
        }
      : /* Null destination clears the category — "everything on this page" is a real place to go. */
        navigation.buildHref({
          category: row.destination?.slug ?? null,
          page: null,
        });

  /* A root category's own landing page has nothing broader to widen to. */
  if (!href) {
    return (
      <li
        aria-current={row.isCurrent ? "page" : undefined}
        className={rowClass}
      >
        {label}
      </li>
    );
  }

  return (
    <li>
      <Link
        href={href}
        prefetch={false}
        aria-current={row.isCurrent ? "page" : undefined}
        className={cn(rowClass, "group")}
        /*
         * Category navigation, whichever shape it wears — these rows are what replaced the
         * subcategory chip row. The slug rather than the title, so the event doesn't change
         * meaning with the viewer's language; a row that clears the category reports nothing,
         * and the service drops that call.
         */
        onClick={() =>
          analyticsService.trackNavigationChipsList(row.destination?.slug)
        }
      >
        {label}
      </Link>
    </li>
  );
};

/**
 * Category as navigation rather than a filter.
 *
 * A landing page's own category cannot be switched off through the URL — the service keeps it
 * and the user only adds — so moving around the taxonomy means going to another landing page.
 * Every row is a real link: up through the ancestors, sideways through the siblings, down into
 * the children. That also keeps it free of the `?category=` duplicates a checkbox produced.
 *
 * On pages that own no category (/new, /sale, a query search) the same widget is the way into
 * the tree: the root categories, then a level at a time, as a filter that preserves what the
 * page is about.
 */
export const CategoryTaxonomy = () => {
  const { t } = useTranslation();
  const { query } = useSearchContext();
  const rows = useTaxonomyRows();

  if (!rows) {
    return null;
  }

  const { path, nextLevel } = rows;
  const anchorPath = query.data?.meta.taxonomy?.path ?? [];
  const anchor = anchorPath[anchorPath.length - 1];
  const mode: Mode =
    anchor && isTermLocked(query.data?.facets ?? [], "category", anchor.slug)
      ? "landing-page"
      : "filter";

  return (
    <FacetSection title={t("listing:filter-category")} defaultOpen>
      <nav aria-label={t("listing:filter-category")}>
        {path.length > 0 && (
          <ul className="flex flex-col">
            {path.map(row => (
              <Row key={row.slug} row={row} mode={mode} />
            ))}
          </ul>
        )}

        {nextLevel.length > 0 && (
          <ul
            className={cn(
              "flex flex-col",
              path.length > 0 && "mt-2 border-t border-grey-100 pt-2"
            )}
          >
            {nextLevel.map(row => (
              <Row key={row.slug} row={row} mode={mode} />
            ))}
          </ul>
        )}
      </nav>
    </FacetSection>
  );
};
